import { useEffect, RefObject } from 'react';

/**
 * Adds rubber-band overscroll to any scroll container.
 *
 * Horizontal (axis='x'): Stretches the edge card (scaleX) and shifts
 * its neighbour to maintain the gap. Feels like pulling a card.
 *
 * Vertical (axis='y'): Translates the whole container with diminishing
 * resistance and springs back. Standard iOS-style rubber-band.
 *
 * Works on both iOS and Android via raw touch events.
 */
export function useRubberBand(
  ref: RefObject<HTMLElement | null>,
  enabled = true,
  axis: 'x' | 'y' = 'y',
) {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    let startPos = 0;
    let pulling = false;
    let edge: 'start' | 'end' | null = null;
    let edgeCard: HTMLElement | null = null;
    let neighbourCard: HTMLElement | null = null;

    const resistance = (d: number) => Math.min(Math.abs(d) / 3, 40);
    const SPRING = 'transform 0.4s cubic-bezier(0.32, 0.72, 0, 1)';

    const getTouch = (e: TouchEvent) =>
      axis === 'x' ? e.touches[0].clientX : e.touches[0].clientY;

    const getScroll = () =>
      axis === 'x' ? el.scrollLeft : el.scrollTop;

    const getMax = () =>
      axis === 'x'
        ? el.scrollWidth - el.clientWidth
        : el.scrollHeight - el.clientHeight;

    // ── Horizontal: stretch edge card ──

    const getEdgeChild = (which: 'start' | 'end'): HTMLElement | null => {
      const children = el.children;
      if (children.length === 0) return null;
      return (which === 'start' ? children[0] : children[children.length - 1]) as HTMLElement;
    };

    const getNeighbour = (which: 'start' | 'end'): HTMLElement | null => {
      const children = el.children;
      if (children.length < 2) return null;
      return (which === 'start' ? children[1] : children[children.length - 2]) as HTMLElement;
    };

    const applyHStretch = (card: HTMLElement, neighbour: HTMLElement | null, amount: number, which: 'start' | 'end', animate: boolean) => {
      const scale = 1 + amount / 300;
      const origin = which === 'start' ? 'left center' : 'right center';

      card.style.transition = animate ? SPRING : 'none';
      card.style.transformOrigin = origin;
      card.style.zIndex = amount > 0 ? '2' : '';
      card.style.transform = amount === 0 && animate ? 'scaleX(1)' : `scaleX(${scale})`;

      // Shift neighbour to maintain gap
      if (neighbour) {
        const growPx = card.offsetWidth * (scale - 1);
        const dir = which === 'start' ? 1 : -1;
        neighbour.style.transition = animate ? SPRING : 'none';
        neighbour.style.transform = amount === 0 && animate
          ? 'translateX(0)'
          : `translateX(${dir * growPx}px)`;
      }
    };

    const resetH = (animate: boolean) => {
      if (edgeCard) applyHStretch(edgeCard, neighbourCard, 0, edge!, animate);
    };

    // ── Vertical: translate whole container ──

    const applyVTranslate = (delta: number, animate: boolean) => {
      const amount = Math.sign(delta) * resistance(delta);
      el.style.transition = animate ? SPRING : 'none';
      el.style.transform = delta === 0 && animate
        ? 'translateY(0)'
        : `translateY(${amount}px)`;
    };

    const resetV = (animate: boolean) => {
      applyVTranslate(0, animate);
    };

    // ── Touch handlers ──

    const onStart = (e: TouchEvent) => {
      startPos = getTouch(e);
      pulling = false;
      edge = null;
      edgeCard = null;
      neighbourCard = null;
    };

    const onMove = (e: TouchEvent) => {
      const delta = getTouch(e) - startPos;
      const scroll = getScroll();
      const max = getMax();
      const atStart = scroll <= 1 && delta > 5;
      const atEnd = scroll >= max - 3 && delta < -5;

      if (atStart || atEnd) {
        const which = atStart ? 'start' as const : 'end' as const;

        if (!pulling) {
          pulling = true;
          edge = which;
          if (axis === 'x') {
            edgeCard = getEdgeChild(which);
            neighbourCard = getNeighbour(which);
          }
        }

        if (axis === 'x' && edgeCard) {
          applyHStretch(edgeCard, neighbourCard, resistance(delta), which, false);
        } else if (axis === 'y') {
          applyVTranslate(delta, false);
        }
      } else if (pulling) {
        if (axis === 'x') resetH(false);
        else resetV(false);
        pulling = false;
        edge = null;
        edgeCard = null;
        neighbourCard = null;
      }
    };

    const onEnd = () => {
      if (!pulling) return;
      if (axis === 'x') resetH(true);
      else resetV(true);
      pulling = false;
      edge = null;
      edgeCard = null;
      neighbourCard = null;
    };

    // Clean up transforms after spring-back
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return;
      const target = e.target as HTMLElement;
      if (target === el || target.parentElement === el) {
        target.style.transform = '';
        target.style.transition = '';
        target.style.transformOrigin = '';
        target.style.zIndex = '';
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    el.addEventListener('transitionend', onTransitionEnd);

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('transitionend', onTransitionEnd);
      el.style.transform = '';
      el.style.transition = '';
    };
  }, [ref, enabled, axis]);
}
